/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useDataProvider } from 'react-admin';
import { Play, Check, AlertTriangle, RotateCcw } from 'lucide-react';
import { logger } from '@musedrops/shared';

interface ProcessLog {
  id: string;
  entity_type: string;
  entity_id: string;
  event_type: string;
  process: string;
  message: string;
}

interface ProcessLogSectionProps {
  entityType: 'story' | 'show';
  entityId: string;
  entityTitle: string;
}

// Event type icon mapping
const EVENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  started: <Play size={14} color="#1976d2" />,
  completed: <Check size={14} color="#15803d" />,
  error: <AlertTriangle size={14} color="#b91c1c" />,
  retry: <RotateCcw size={14} color="#ff9800" />,
};

export const ProcessLogSection: React.FC<ProcessLogSectionProps> = ({
  entityType,
  entityId,
  entityTitle,
}) => {
  const dataProvider = useDataProvider();
  const [logs, setLogs] = useState<ProcessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [lastError, setLastError] = useState<ProcessLog | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);

        // Fetch process logs for this entity
        const { data } = await dataProvider.getList('process_logs', {
          filter: {
            entity_type: entityType,
            entity_id: parseInt(entityId),
          },
          sort: { field: 'id', order: 'DESC' },
          pagination: { page: 1, perPage: showAll ? 100 : 10 },
        });

        setLogs(data);

        // Find the last error
        const errorLog = data.find(
          (log: ProcessLog) => log.event_type === 'error'
        );
        setLastError(errorLog || null);
      } catch (error) {
        logger.error('Failed to fetch process logs', {
          entityType,
          entityId,
          error,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [dataProvider, entityType, entityId, showAll]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getEventTypeDisplay = (eventType: string) => {
    const icon = EVENT_TYPE_ICONS[eventType] || null;
    const label = eventType.charAt(0).toUpperCase() + eventType.slice(1);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        {label}
      </Box>
    );
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'error':
        return 'error';
      case 'completed':
        return 'success';
      case 'started':
        return 'info';
      case 'retry':
        return 'warning';
      default:
        return 'default';
    }
  };

  const toggleMessageExpanded = (logId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const truncateMessage = (message: string, logId: string) => {
    const maxLength = 100;
    const isExpanded = expandedMessages.has(logId);

    if (!message || message.length <= maxLength) {
      return message;
    }

    if (isExpanded) {
      return (
        <>
          {message}{' '}
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={e => {
              e.stopPropagation();
              toggleMessageExpanded(logId);
            }}
          >
            ◀︎◀︎
          </Typography>
        </>
      );
    }

    return (
      <>
        {message.slice(0, maxLength)}{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            fontSize: 'inherit',
          }}
          onClick={e => {
            e.stopPropagation();
            toggleMessageExpanded(logId);
          }}
        >
          ▶︎▶︎
        </Typography>
      </>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Typography variant="h6" gutterBottom>
        Process Logs
      </Typography>

      {/* Show last error prominently if exists */}
      {lastError && (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: '#ffebee',
            border: '1px solid #e57373',
          }}
        >
          <Typography variant="subtitle2" color="error" gutterBottom>
            ‼️ Last Error
          </Typography>
          <Typography variant="body2">{lastError.message}</Typography>
          <Typography variant="caption" color="text.secondary">
            Log ID: {lastError.id}
          </Typography>
        </Paper>
      )}

      {logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No process logs found for {entityTitle}
        </Typography>
      ) : (
        <>
          <List disablePadding>
            {logs.map(log => (
              <ListItem
                key={log.id}
                sx={{
                  borderBottom: '1px solid #e0e0e0',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={getEventTypeDisplay(log.event_type)}
                        size="small"
                        color={getEventTypeColor(log.event_type) as any}
                        variant="outlined"
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {log.process}
                      </Typography>
                      {log.message && (
                        <Typography variant="body2">
                          - {truncateMessage(log.message, log.id)}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={`Log ID: ${log.id}`}
                />
              </ListItem>
            ))}
          </List>

          {logs.length >= 10 && !showAll && (
            <Box mt={2} textAlign="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAll(true)}
              >
                Show More Logs
              </Button>
            </Box>
          )}

          {showAll && logs.length > 10 && (
            <Box mt={2} textAlign="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAll(false)}
              >
                Show Less
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProcessLogSection;
